import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ChatInput } from './ChatInput';
import { motion } from 'framer-motion';
import { Layers, Smartphone, FileText, Clock, Globe, RefreshCw } from 'lucide-react';

export const Hero = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fullstack');

  const tabs = [
    { id: 'fullstack', label: t('hero.tabs.fullstack'), icon: Layers, placeholder: t('hero.placeholders.fullstack') },
    { id: 'mobile', label: t('hero.tabs.mobile'), icon: Smartphone, placeholder: t('hero.placeholders.mobile') },
    { id: 'landing', label: t('hero.tabs.landing'), icon: FileText, placeholder: t('hero.placeholders.landing') },
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  const recentProjects = [
    { name: 'MoltBot', isNew: true },
    { name: 'Consult Plus', isNew: false },
    { name: 'Elite Footwear', isNew: false },
    { name: 'Smart Course', isNew: false },
  ];

  const handleSubmit = (prompt) => {
    if (!user) {
      navigate('/register');
      return;
    }
    // Navigate to dashboard with prompt
    navigate('/dashboard', { state: { prompt, type: activeTab } });
  };

  return (
    <section className="hero-bg min-h-screen pt-24 lg:pt-32 pb-16 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-base lg:text-lg text-gray-400 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-item flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                data-testid={`hero-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </motion.div>

        {/* Chat Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <ChatInput
            placeholder={activeTabData?.placeholder}
            onSubmit={handleSubmit}
            projectType={activeTab}
          />
        </motion.div>

        {/* Recent Projects Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-16"
        >
          {recentProjects.map((project, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm"
            >
              {project.isNew ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span className="text-white font-medium">{project.name}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400">New</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">{project.name}</span>
                </span>
              )}
            </div>
          ))}
        </motion.div>

        {/* Bottom Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center items-center gap-6 text-sm text-gray-500"
        >
          <button className="flex items-center gap-2 hover:text-white transition-colors" data-testid="recent-tasks-btn">
            <Clock className="w-4 h-4" />
            {t('hero.recentTasks')}
          </button>
          <span className="w-px h-4 bg-white/10"></span>
          <button className="flex items-center gap-2 hover:text-white transition-colors" data-testid="deployed-apps-btn">
            <Globe className="w-4 h-4" />
            {t('hero.deployedApps')}
          </button>
          <button className="p-1.5 hover:text-white transition-colors" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
